/**
 * 全局图片懒加载指令 v-lazy-img
 * 
 * 用法：
 *   <img v-lazy-img="imageSource" alt="..." />
 *   或带占位图：
 *   <img v-lazy-img="{ src: imageSource, placeholder: placeholderSrc }" alt="..." />
 * 
 * 首屏关键图片（hero 轮播图等）不要使用此指令，保持原生 :src
 * 
 * 增强：自动检测 WebP 支持，优先加载 .webp 版本（体积更小）
 */
import Vue from 'vue'
import { supportsWebP } from './webp'

// 占位图（1x1 透明像素的 base64，零请求）
const EMPTY_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

/**
 * 将图片路径转为 WebP（如果浏览器支持且是 jpg/png）
 * @param {string} src
 * @returns {string}
 */
function toWebp(src) {
  if (!src || !supportsWebP()) return src
  const match = src.match(/\.(jpg|jpeg|png)$/i)
  if (match) {
    return src.substring(0, src.length - match[0].length) + '.webp'
  }
  return src
}

// 全局唯一的 Observer 实例，所有懒加载图片共享
let globalObserver = null
// 存储所有待观察元素及其回调
const observerMap = new WeakMap()

function ensureObserver() {
  if (globalObserver) return globalObserver

  globalObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const handler = observerMap.get(entry.target)
          if (handler) {
            handler()
            globalObserver.unobserve(entry.target)
            observerMap.delete(entry.target)
          }
        }
      })
    },
    {
      // rootMargin: 上下各扩展 300px，提前开始加载
      rootMargin: '300px 0px 300px 0px',
      threshold: 0.01,
    }
  )

  return globalObserver
}

const lazyLoadDirective = {
  inserted(el, binding) {
    // 解析参数：支持字符串（直接 src）或对象 { src, placeholder }
    let src = ''
    let placeholder = EMPTY_PLACEHOLDER

    if (typeof binding.value === 'string') {
      src = binding.value
    } else if (binding.value && typeof binding.value === 'object') {
      src = binding.value.src || ''
      placeholder = binding.value.placeholder || EMPTY_PLACEHOLDER
    }

    if (!src) return

    // 先设置占位图
    el.src = placeholder

    // 如果图片已有 loading 完成事件处理，保留
    const onLoad = () => {
      el.classList.add('lazy-loaded')
    }
    const onError = () => {
      // 加载失败时保持占位图
      el.classList.add('lazy-error')
    }

    const loadImage = () => {
      // 优先使用 WebP（浏览器支持时）
      const finalSrc = toWebp(src)
      // 创建临时 Image 对象预加载，加载成功后再设置到真实元素
      const tempImg = new Image()
      tempImg.onload = () => {
        el.src = finalSrc
        onLoad()
      }
      tempImg.onerror = () => {
        // WebP 加载失败时降级到原图
        if (finalSrc !== src) {
          el.src = src
        } else {
          el.src = src
        }
        onError()
      }
      tempImg.src = finalSrc
    }

    const observer = ensureObserver()
    observer.observe(el)
    observerMap.set(el, loadImage)

    // 保存清理引用
    el._lazyCleanup = () => {
      if (observerMap.has(el)) {
        globalObserver.unobserve(el)
        observerMap.delete(el)
      }
    }
  },

  update(el, binding) {
    // src 更新时重新处理
    if (binding.value === binding.oldValue) return

    // 先清理旧观察
    if (el._lazyCleanup) {
      el._lazyCleanup()
    }

    let src = ''
    let placeholder = EMPTY_PLACEHOLDER

    if (typeof binding.value === 'string') {
      src = binding.value
    } else if (binding.value && typeof binding.value === 'object') {
      src = binding.value.src || ''
      placeholder = binding.value.placeholder || EMPTY_PLACEHOLDER
    }

    if (!src) return

    el.src = placeholder

    const loadImage = () => {
      const finalSrc = toWebp(src)
      const tempImg = new Image()
      tempImg.onload = () => {
        el.src = finalSrc
        el.classList.add('lazy-loaded')
      }
      tempImg.onerror = () => {
        el.src = finalSrc !== src ? src : src
        el.classList.add('lazy-error')
      }
      tempImg.src = finalSrc
    }

    const observer = ensureObserver()
    observer.observe(el)
    observerMap.set(el, loadImage)

    el._lazyCleanup = () => {
      if (observerMap.has(el)) {
        globalObserver.unobserve(el)
        observerMap.delete(el)
      }
    }
  },

  unbind(el) {
    if (el._lazyCleanup) {
      el._lazyCleanup()
      delete el._lazyCleanup
    }
  },
}

// 注册全局指令
Vue.directive('lazy-img', lazyLoadDirective)

export default lazyLoadDirective
